import { h, Component } from 'preact';
import DeviceDetector from 'device-detector-js';
import Store from 'store/store';
import { accessNested, commaSeparatedList, joinClasses } from 'utils/utils';
import { Camera, PhotoUpload, CameraFlip, CameraLoad } from 'components/bolt/icons/quizPhotoboothIcons';
import { selfieSegmentApi, stripBase64Info, addBase64Info } from 'utils/bolt/quizPhotobooth';
import Loader from 'components/bolt/quizPhotobooth/loader';
import PhotoEdit from 'components/bolt/quizPhotobooth/photoEdit';
import HTML from 'components/core/html/html';
import s from 'components/bolt/quizPhotobooth/photobooth.sss';

// shows which screen to go back to when back button is clicked, based on active screen
const previousScreens = {
	welcome: 'welcome',
	browserNotAllowed: 'welcome',
	cameraPermissionsDenied: 'welcome',
	cameraError: 'welcome',
	cameraStream: 'welcome',
	photoEdit: 'cameraStream',
};

const browsersWithIcon = new Set(['chrome', 'firefox', 'safari', 'edge']);

const getFileAsBlob = async (file) => new Promise((resolve, reject) => {
	const reader = new FileReader();
	reader.onload = () => resolve(new Blob([reader.result]));
	reader.onerror = () => reject(new Error('Error reading file'));
	reader.readAsArrayBuffer(file);
});
const loadImage = async (src) => new Promise((resolve, reject) => {
	let img = new Image();
	img.onload = () => resolve(img);
	img.onerror = () => reject(new Error(`Error loading image ${src}`));
	img.src = src;
});

export default class Photobooth extends Component {
	constructor(props) {
		super(props);
		this.backPhotoStep = this.backPhotoStep.bind(this);
		this.checkDevice = this.checkDevice.bind(this);
		this.onWindowResize = this.onWindowResize.bind(this);
		this.setUpStream = this.setUpStream.bind(this);
		this.drawCameraFeed = this.drawCameraFeed.bind(this);
		this.takePhoto = this.takePhoto.bind(this);
		this.onSetUpStreamClick = this.onSetUpStreamClick.bind(this);
		this.flipCamera = this.flipCamera.bind(this);
		this.onUploadPhoto = this.onUploadPhoto.bind(this);
		this.reset = this.reset.bind(this);
		this.finishPhotoEdit = this.finishPhotoEdit.bind(this);
		this.onSelectedBrowserClick = this.onSelectedBrowserClick.bind(this);
		this.initialState = {
			activeScreen: 'welcome',
			waitingForCamera: false,
			showCameraFlip: false,
			flipping: false,
			facingUser: true,
			photoTaken: false,
			headshotData: null,
			captureOrUpload: null,
			cameraLoading: false,
		};
		this.state = this.initialState;
	}

	get appData() {
		return this.props.appData || Store.get().appData;
	}
	get fontStyles() {
		return this.appData?.styling?.fontVariables || {};
	}
	get copy() {
		return this.appData?.copy?.baseCopy || {};
	}

	componentDidMount() {
		Store.on('QUIZPHOTOBOOTH_PHOTOBOOTH_RESET', this.reset);
		this.checkDevice();
		this.pixelRatio = window.devicePixelRatio || 1;
		this.cameraContext = this.$cameraCanvas.getContext('2d');
		this.snapshotContext = this.$snapshotCanvas.getContext('2d');
		this.setCanvasSize();
		window.addEventListener('resize', this.onWindowResize);

		if ((__IS_LOCAL__ || __IS_DEMO__) && this.props?.testStep) {
			if (this.props.testStep === 'browserErrorScreen') {
				this.setState({ activeScreen: 'browserNotAllowed' });
			}
			if (this.props.testStep === 'cameraDeniedScreen') {
				this.setState({ activeScreen: 'cameraPermissionsDenied' });
			}
			if (this.props.testStep === 'deviceErrorScreen') {
				this.setState({ activeScreen: 'cameraError' });
			}
		}
	}

	componentWillUnmount() {
		Store.off('QUIZPHOTOBOOTH_PHOTOBOOTH_RESET', this.reset);
		window.removeEventListener('resize', this.onWindowResize);
		window.cancelAnimationFrame(this.drawCameraFeed);
		this.waitForFlashTimer && clearTimeout(this.waitForFlashTimer);
		this.flipTimer && clearTimeout(this.flipTimer);
	}

	backPhotoStep() {
		if (this.state.activeScreen === 'welcome') {
			this.props.backStep();
		} else {
			if (this.state.activeScreen === 'cameraStream') {
				this.stopStream();
				this.setState({ captureOrUpload: null });
			}
			if (this.state.activeScreen === 'photoEdit') {
				if (this.state.captureOrUpload === 'capture') {
					this.cameraContext?.clearRect(0, 0, this.$cameraCanvas.width, this.$cameraCanvas.height);
					this.setUpStream(this.state.facingUser);
				}
				this.setState({ photoTaken: false });
				this.photoDrawn = false;
				if (this.state.captureOrUpload === 'upload') {
					this.setState({ activeScreen: 'welcome' });
				}
			}
			this.setState({ activeScreen: previousScreens[this.state.activeScreen] || 'welcome' });
		}
	}

	onWindowResize() {
		this.setCanvasSize();
	}

	onErrorBackClick(screen) {
		// this.reset();
		this.backPhotoStep();
		Store.emit('TRACK_QUIZPHOTOBOOTH_ERROR_BACK', screen);
	}

	setCanvasSize() {
		this.$cameraCanvas.width = this.$cameraCanvas.clientWidth * this.pixelRatio;
		this.$cameraCanvas.height = this.$cameraCanvas.clientHeight * this.pixelRatio;
	}

	checkDevice() {
		if (!this.props.allowedBrowsers) {
			return;
		}
		const deviceDetector = new DeviceDetector();

		// Note: we should not blanket block some browsers, but instead detect the features that might cause issues if not supported
		const device = deviceDetector.parse(navigator.userAgent);
		const isOldIOS = device.os.name === 'iOS' && device.os.version < 9;
		const isOldAndroid = device.os.name === 'Android' && device.os.version < 7.0;
		const isSamsungBrowser = device.client.name === 'Samsung Browser';
		const isUnsupportedDesktop = device.device.type === 'desktop'
			&& !commaSeparatedList(this.props.allowedBrowsers).includes(device.client.name);

		if (isOldAndroid || isOldIOS || isSamsungBrowser || isUnsupportedDesktop) {
			this.setState({ activeScreen: 'browserNotAllowed' });
			Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', 'browser-error');
		}
	}

	async processImage(img, options) {
		img = stripBase64Info(img);
		if (this.state.activeScreen === 'cameraStream') {
			this.stopStream();
		}
		this.setState({ activeScreen: 'loading', processingProgress: 0 });

		if (!this.props.photobooth.backgroundRemoval) {
			return this.setHeadshotData(img);
		}
		// if 'Photo Background Removal' is toggled on, send image to API to remove background
		try {
			const data = await selfieSegmentApi({
				method: 'POST',
				body: {
					img,
					crop: options?.crop || false,
					// squareify: true
				},
				onProgress: ({ progress }) => this.setState({ processingProgress: progress })
			});
			this.setHeadshotData(data.img);
		} catch (e) {
			console.error('Error removing background', e);
			this.setHeadshotData(img);
		}
	}

	setHeadshotData(img) {
		const headshotData = addBase64Info(img);
		this.setState({
			activeScreen: 'photoEdit',
			headshotData
		});
		setTimeout(() => this.setState({ cameraLoading: false }), 500);
		if (this.fileInput.value) {
			this.fileInput.value = null;
		}
		Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', 'resize');
	}

	onSetUpStreamClick() {
		Store.emit('TRACK_QUIZPHOTOBOOTH_UPLOAD_OR_TAKE_PHOTO', 'take-photo');
		this.setUpStream(this.state.facingUser);
	}

	async onUploadPhoto(event) {
		Store.emit('TRACK_QUIZPHOTOBOOTH_UPLOAD_OR_TAKE_PHOTO', 'upload');
		try {
			const dataURL = await this.compressImage(event.target.files[0]);
			this.setState({ captureOrUpload: 'upload' });
			this.processImage(dataURL, { crop: false });
		} catch (e) {
			// Show error? Unable to process image
		}
	}

	async loadAnyImage(data) {
		if (data instanceof HTMLImageElement) {
			if (data.complete) {
				return data;
			}
			return new Promise((res, rej) => {
				data.addEventListener('load', () => res(data), { once: true });
				data.addEventListener('error', () => rej(new Error('Error loading image')), { once: true });
			});
		}
		// File -> Blob -> url (string)
		// But also allow providing any of the intermediary steps
		if (data instanceof File) {
			data = await getFileAsBlob(data);
		}
		if (data instanceof Blob) {
			data = URL.createObjectURL(data);
		}
		if (typeof data === 'string') {
			// URL of some sort (http, base64, blob, ...)
			return loadImage(data);
		}
		throw new Error('Invalid image');
	}

	async compressImage(source) {
		const img = await this.loadAnyImage(source);
		const srcW = img.naturalWidth;
		const srcH = img.naturalHeight;
		const ratio = srcW / srcH;
		// Max 1500x1500
		let destW = Math.min(srcW, 1500);
		let destH = Math.min(srcH, 1500);
		// Keep the original aspect ratio while keeping both sides in the range
		if (ratio > destW / destH) {
			destH = destW / ratio;
		} else {
			destW = destH * ratio;
		}
		const canvas = document.createElement('canvas');
		canvas.width = destW;
		canvas.height = destH;
		canvas.getContext('2d').drawImage(img, 0, 0, destW, destH);
		return canvas.toDataURL('image/jpeg', 0.8);
	}

	reset() {
		this.photoDrawn = false;
		this.camerasFound = false;
		this.setState({
			...this.initialState,
		});
	}

	finishPhotoEdit() {
		return this.props.advanceStep();
	}

	findCameras() {
		window.navigator.mediaDevices.enumerateDevices().then(devices => {
			let cameras = Object.values(devices).filter(device => {
				return device.kind === 'videoinput';
			});
			if (cameras.length >= 2) {
				this.setState({ showCameraFlip: true });
			}
			this.camerasFound = true;
		});
	}

	flipCamera() {
		const newFacingUser = !this.state.facingUser;
		this.setState({ facingUser: newFacingUser, flipping: true }),
		this.flipTimer = setTimeout(() => {
			this.setState({ flipping: false });
		}, 600);
		this.stopStream();
		this.setUpStream(newFacingUser);
		Store.emit('TRACK_QUIZPHOTOBOOTH_FLIP_CAMERA');
	}

	async setUpStream(facingUser) {
		this.setState({ waitingForCamera: true });

		if (!navigator.mediaDevices?.getUserMedia) {
			return;
		}
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: facingUser ? 'user' : 'environment' }
			});
			this.$cameraVideo.srcObject = stream;
			if (!this.camerasFound) {
				this.findCameras();
				Store.emit('TRACK_QUIZPHOTOBOOTH_CAMERA_PERMISSIONS', 'allow');
			}
			this.drawCameraFeed();
		} catch (e) {
			this.setState({ waitingForCamera: false });
			if (e.message?.includes('denied')) {
				this.setState({ activeScreen: 'cameraPermissionsDenied' });
				Store.emit('TRACK_QUIZPHOTOBOOTH_CAMERA_PERMISSIONS', 'deny');
				Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', 'camera-denied');
			} else {
				this.setState({ activeScreen: 'cameraError' });
				Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', 'device-error');
			}
		}
	}

	stopStream() {
		cancelAnimationFrame(this.drawCameraLoop);
		this.$cameraVideo.srcObject.getTracks().forEach(track => track.stop());
		this.$cameraVideo.srcObject = null;
	}

	takePhoto() {
		this.setState({ photoTaken: true, cameraLoading: true });
		this.$cameraVideo?.pause();
		Store.emit('TRACK_QUIZPHOTOBOOTH_CAPTURE_PHOTO');
		this.setState({ captureOrUpload: 'capture' });
	}

	drawPhoto() {
		let coords = {
			x1: 0,
			x2: this.$cameraCanvas.width,
			y1: 0,
			y2: this.$cameraCanvas.height
		};
		if (this.props.photobooth.photoWindow) {
			coords = this.getPhotoWindowCoordinates();
			coords.x1 *= this.pixelRatio;
			coords.x2 *= this.pixelRatio;
			coords.y1 *= this.pixelRatio;
			coords.y2 *= this.pixelRatio;
		}
		this.snapshotContext.drawImage(
			this.$cameraCanvas,
			coords.x1,
			coords.y1,
			coords.x2 - coords.x1,
			coords.y2 - coords.y1,
			0,
			0,
			this.$snapshotCanvas.width,
			this.$snapshotCanvas.height
		);
		const imageData = this.$snapshotCanvas.toDataURL('image/jpeg', 0.8);
		this.waitForFlashTimer = setTimeout(() => {
			this.processImage(imageData, { crop: false });
		}, 350);
		this.photoDrawn = true;
	}

	calculateClipSize(srcSize, destSize) {
		const widthRatio = srcSize.width / destSize.width;
		const heightRatio = srcSize.height / destSize.height;
		let clipWidth;
		let clipHeight;
		if (widthRatio < heightRatio) {
			clipWidth = srcSize.width;
			clipHeight = destSize.height * widthRatio;
		} else {
			clipHeight = srcSize.height;
			clipWidth = destSize.width * heightRatio;
		}
		return {
			width: clipWidth,
			height: clipHeight
		};
	}

	getPhotoWindowCoordinates() {
		const canvasStyleWidth = this.$cameraCanvas.clientWidth;
		const photoSide = canvasStyleWidth - canvasStyleWidth / 4;
		const x1 = (canvasStyleWidth - photoSide) / 2;
		const x2 = x1 + photoSide;
		const y1 = canvasStyleWidth / 8;
		const y2 = y1 + photoSide;
		return { x1, x2, y1, y2 };
	}

	drawPhotoWindow() {
		const { x1, x2, y1, y2 } = this.getPhotoWindowCoordinates();
		const photoSide = x2 - x1;
		this.cameraContext.strokeStyle = '#fff';
		this.cameraContext.lineWidth = 2;
		this.cameraContext.scale(this.pixelRatio, this.pixelRatio);

		// top left
		this.drawPhotoWindowCurve(
			{ x: x1, y: y1 + (1 / 3 * photoSide) },
			{ x: x1, y: y1 },
			{ x: x1 + (1 / 3 * photoSide), y: y1 }
		);

		// top right
		this.drawPhotoWindowCurve(
			{ x: x1 + (2 / 3 * photoSide), y: y1 },
			{ x: x2, y: y1 },
			{ x: x2, y: y1 + (1 / 3 * photoSide) }
		);

		// bottom left
		this.drawPhotoWindowCurve(
			{ x: x1, y: y2 - (1 / 3 * photoSide) },
			{ x: x1, y: y2 },
			{ x: x1 + (1 / 3 * photoSide), y: y2 }
		);

		// bottom right
		this.drawPhotoWindowCurve(
			{ x: x1 + (2 / 3 * photoSide), y: y2 },
			{ x: x2, y: y2 },
			{ x: x2, y: y2 - (1 / 3 * photoSide) }
		);
	}

	drawPhotoWindowCurve(start, corner, end) {
		this.cameraContext.beginPath();
		this.cameraContext.moveTo(start.x, start.y);
		this.cameraContext.arcTo(corner.x, corner.y, end.x, end.y, 30);
		this.cameraContext.lineTo(end.x, end.y);
		this.cameraContext.stroke();
	}

	drawCameraFeed() {
		if (!this.$cameraVideo || this.$cameraVideo.readyState !== this.$cameraVideo.HAVE_ENOUGH_DATA) {
			return this.drawCameraLoop = requestAnimationFrame(this.drawCameraFeed);
		}
		if (this.state.waitingForCamera) {
			this.setState({ waitingForCamera: false });
			this.setState({ activeScreen: 'cameraStream' });
		}
		const { facingUser } = this.state;
		this.setCanvasSize();
		const videoSize = { width: this.$cameraVideo.videoWidth, height: this.$cameraVideo.videoHeight };
		const canvasSize = { width: this.$cameraCanvas.width, height: this.$cameraCanvas.height };
		const clipSize = this.calculateClipSize(videoSize, canvasSize);
		const srcX = (videoSize.width - clipSize.width) / 2;
		const srcY = (videoSize.height - clipSize.height) / 2;
		this.cameraContext.clearRect(0, 0, this.$cameraCanvas.width, this.$cameraCanvas.height);
		this.cameraContext.save();
		if (facingUser) {
			this.cameraContext.scale(-1, 1);
		}
		this.cameraContext.drawImage(
			this.$cameraVideo,
			srcX,
			srcY,
			clipSize.width,
			clipSize.height,
			0,
			0,
			facingUser ? canvasSize.width * -1 : canvasSize.width,
			canvasSize.height
		);
		this.cameraContext.restore();
		if (this.state.photoTaken && !this.photoDrawn) {
			this.drawPhoto();
		}
		this.props.photobooth.photoWindow && this.drawPhotoWindow();

		this.drawCameraLoop = requestAnimationFrame(this.drawCameraFeed);
	}

	onSelectedBrowserClick(browser) {
		const { isDesktopDevice } = this.props;
		if (!isDesktopDevice) {
			return;
		}

		const name = browser.toLowerCase();
		let path = '';
		if (name === 'chrome') {
			path = 'https://www.google.com/intl/en_uk/chrome/';
		} else if (name === 'firefox') {
			path = 'https://www.mozilla.org/en-GB/firefox/new/';
		} else if (name === 'safari') {
			path = 'https://www.apple.com/uk/safari/';
		} else if (name === 'edge') {
			path = 'https://www.microsoft.com/en-us/edge/download?form=MA13FJ';
		}
		let link = document.createElement('a');
		link.href = path;
		link.target = '_blank';
		link.style.display = 'none';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		Store.emit('TRACK_QUIZPHOTOBOOTH_BROWSER_ERROR_CLICK', screen, browser);
	}

	renderBrowserNotAllowed() {
		const browserNotAllowed = this.state.activeScreen === 'browserNotAllowed';
		const copy = this.copy;
		const message = copy.$QUIZPHOTOBOOTH_BROWSER_NOT_ALLOWED;
		let sharedImageRoot = this.appData?.urls?.roots?.sharedImages || '';
		const allowedBrowsers = commaSeparatedList(this.props.allowedBrowsers?.toLowerCase());
		return (
			<div class={joinClasses(s.permissionsDeniedContainer, browserNotAllowed && s.show)}>
				<HTML class={joinClasses(s.permissionsDeniedTitle, this.fontStyles.quizPhotoboothTitle)} content={copy.$QUIZPHOTOBOOTH_BROWSER_NOT_ALLOWED_TITLE} markdown />
				<HTML class={joinClasses(s.permissionsDeniedMessage, this.fontStyles.quizPhotoboothMessage)} content={message} markdown />
				<div class={s.browsers}>
					{allowedBrowsers.filter(browsersWithIcon.has, browsersWithIcon).map(browser => (
						<button class={s.browserImage} onClick={() => this.onSelectedBrowserClick(browser)}>
							<img src={`${sharedImageRoot}/browsers/${browser}.png`} />
						</button>
					))}
				</div>
				{this.renderErrorBackBtn('camera')}
			</div>
		);
	}

	renderCameraPermissionsDenied() {
		const cameraPermissionsDenied = this.state.activeScreen === 'cameraPermissionsDenied';
		const { isDesktopDevice } = this.props;
		const copy = this.copy;
		const message = isDesktopDevice ? copy.$QUIZPHOTOBOOTH_CAMERA_ACCESS_DENIED_DESKTOP : copy.$QUIZPHOTOBOOTH_CAMERA_ACCESS_DENIED_MOBILE;
		return (
			<div class={joinClasses(s.permissionsDeniedContainer, cameraPermissionsDenied && s.show)}>
				<HTML class={joinClasses(s.permissionsDeniedTitle, this.fontStyles.quizPhotoboothTitle)} content={copy.$QUIZPHOTOBOOTH_CAMERA_ACCESS_DENIED_TITLE} markdown />
				<HTML class={joinClasses(s.permissionsDeniedMessage, this.fontStyles.quizPhotoboothMessage)} content={message} markdown />
				{this.renderErrorBackBtn('camera')}
			</div>
		);
	}

	renderError() {
		let { isDesktopDevice } = this.props;
		const { client } = Store.get();
		const cameraError = this.state.activeScreen === 'cameraError';
		const copy = this.copy;
		const fontStyles = this.fontStyles;

		const isiPhone = client.device === 'iphone' || client.device === 'ipad';
		const isAndroid = client.device === 'android';

		const defaultErrorScreen = () => (
			<div>
				<HTML class={joinClasses(s.deviceReqTitle, fontStyles.quizPhotoboothQuestion)} content={copy.$QUIZPHOTOBOOTH_DEVICE_IOS} markdown />
				{copy.$QUIZPHOTOBOOTH_DEVICE_IOS_REQS.split(', ').map(req => <HTML class={joinClasses(s.req, fontStyles.quizPhotoboothMessage)} content={req} markdown />)}
				<HTML class={joinClasses(s.deviceReqTitle, s.android, fontStyles.quizPhotoboothQuestion)} content={copy.$QUIZPHOTOBOOTH_DEVICE_ANDROID} markdown />
				{copy.$QUIZPHOTOBOOTH_DEVICE_ANDROID_REQS.split(', ').map(req => <HTML class={joinClasses(s.req, fontStyles.quizPhotoboothMessage)} content={req} markdown />)}
			</div>
		);
		const androidErrorScreen = () => (
			<div>
				<HTML class={joinClasses(s.deviceReqTitle, s.android, fontStyles.quizPhotoboothQuestion)} content={copy.$QUIZPHOTOBOOTH_DEVICE_ANDROID} markdown />
				{copy.$QUIZPHOTOBOOTH_DEVICE_ANDROID_REQS.split(', ').map(req => <HTML class={joinClasses(s.req, fontStyles.quizPhotoboothMessage)} content={req} markdown />)}
			</div>
		);
		const iOSErrorScreen = () => (
			<div>
				<HTML class={joinClasses(s.deviceReqTitle, fontStyles.quizPhotoboothQuestion)} content={copy.$QUIZPHOTOBOOTH_DEVICE_IOS} markdown />
				{copy.$QUIZPHOTOBOOTH_DEVICE_IOS_REQS.split(', ').map(req => <HTML class={joinClasses(s.req, fontStyles.quizPhotoboothMessage)} content={req} markdown />)}
			</div>
		);

		const displayError = () => {
			if (isDesktopDevice) {
				return defaultErrorScreen();
			}
			if (isiPhone) {
				return iOSErrorScreen();
			}
			if (isAndroid) {
				return androidErrorScreen();
			}
		};

		return (
			<div class={joinClasses(s.errorContainer, cameraError && s.show)}>
				<div class={s.errorContent}>
					<HTML class={joinClasses(s.errorTitle, fontStyles.quizPhotoboothTitle)} content={copy.$QUIZPHOTOBOOTH_DEVICE_REQ_TITLE} markdown />
					{displayError()}
				</div>
				{this.renderErrorBackBtn('device')}
			</div>
		);
	}

	renderErrorBackBtn(screen) {
		return (
			<button class={joinClasses(s.longBtn, s.errorBackBtn)} onClick={() => this.onErrorBackClick(screen)}>
				<span class={this.fontStyles.quizPhotoboothButton}>{this.copy.$QUIZPHOTOBOOTH_BACK}</span>
			</button>
		);
	}

	renderWelcomePage() {
		const { quizResult, resultData, isDesktopWidth, photobooth, noQuiz } = this.props;

		const showContainer = this.state.activeScreen === 'welcome';

		const quizResultData = quizResult ? resultData[quizResult] : Object.values(resultData)[0];

		const copy = this.copy;
		const fontStyles = this.fontStyles;
		// const showContent = this.props.noQuiz || revealAnimEnded;
		const welcomeImageSrc = isDesktopWidth ? photobooth?.welcomeImageDesktop : (photobooth?.welcomeImageMobile || photobooth?.welcomeImageDesktop);
		const welcomeImageAltText = photobooth?.welcomeImageAltText;
		const revealImageSrc = isDesktopWidth ? quizResultData?.revealImageDesktop : quizResultData?.revealImageMobile;

		return (
			<div class={joinClasses(s.welcomeContainer, showContainer && s.show)}>
				<img class={s.welcomeImage} src={noQuiz ? welcomeImageSrc : revealImageSrc} alt={welcomeImageAltText && welcomeImageAltText} />
				<HTML class={joinClasses(s.title, fontStyles.quizPhotoboothHeader)} content={copy.$QUIZPHOTOBOOTH_PHOTOBOOTH_HEADER} markdown />
				<HTML class={joinClasses(s.instructions, fontStyles.quizPhotoboothCopy)} content={copy.$QUIZPHOTOBOOTH_PHOTOBOOTH_SUBHEADER} markdown />
				<div class={joinClasses(s.actionButtons)}>
					<button class={s.btn} onClick={this.onSetUpStreamClick}>
						<Camera class={s.icon} />
						<HTML class={joinClasses(s.cta, fontStyles.quizPhotoboothCopy)} content={copy.$QUIZPHOTOBOOTH_TAKE_PHOTO} markdown />
					</button>
					<label class={s.btn}>
						<PhotoUpload class={s.icon} />
						<HTML class={joinClasses(s.cta, s.upload, fontStyles.quizPhotoboothCopy)} content={copy.$QUIZPHOTOBOOTH_UPLOAD} markdown />
						<input
							type="file"
							class={s.fileInput}
							accept="image/png, image/jpeg, image/webp"
							onChange={this.onUploadPhoto}
							ref={ref => this.fileInput = ref}
						/>
					</label>
				</div>
			</div>
		);
	}

	renderTips() {
		const copy = this.copy;
		const fontStyles = this.fontStyles;
		return (
			<div class={s.tipsContainer} key="tips">
				<HTML class={joinClasses(s.picTip, fontStyles.quizPhotoboothMessage)} content={copy.$QUIZPHOTOBOOTH_PIC_TIP_HEADER} markdown />
				<HTML class={joinClasses(s.picTip, fontStyles.quizPhotoboothCopy)} content={copy.$QUIZPHOTOBOOTH_PIC_TIP_1} markdown />
				<HTML class={joinClasses(s.picTip, fontStyles.quizPhotoboothCopy)} content={copy.$QUIZPHOTOBOOTH_PIC_TIP_2} markdown />
			</div>
		);
	}

	renderCameraFeed() {
		const { photoTaken, flipping } = this.state;
		const showCameraStream = this.state.activeScreen === 'cameraStream';
		const copy = this.copy;
		const fontStyles = this.fontStyles;
		return (
			<div class={joinClasses(s.cameraFeedContainer, showCameraStream && s.show)}>
				<HTML class={joinClasses(s.picTip, s.picPosition, fontStyles.quizPhotoboothMessage)} content={copy.$QUIZPHOTOBOOTH_POSITION} markdown />
				<div class={joinClasses(s.cameraFeed, photoTaken && s.flash, flipping && s.flipping)}>
					<video ref={($ref) => this.$cameraVideo = $ref} class={s.cameraVideo} autoPlay playsInline />
					<canvas ref={($ref) => this.$cameraCanvas = $ref} class={s.cameraCanvas} />
					<div class={s.cameraOverlay} />
				</div>
				<div>
					<div class={joinClasses(s.buttonContainer, flipping && s.flipping)}>
						<div class={s.captureContainer}>
							{this.state.cameraLoading ? (
								<button class={joinClasses(s.cameraButton, s.spin)}><CameraLoad class={s.icon} /></button>
							) : (
								<button class={s.cameraButton} onClick={this.takePhoto}><Camera class={s.icon} /></button>
							)}
						</div>
						{this.state.showCameraFlip && (
							<button class={joinClasses(s.cameraFlipBtn, this.state.cameraLoading && s.fadeOut)} onClick={this.flipCamera}><CameraFlip class={s.flipIcon} /></button>
						)}
					</div>
					{this.renderTips()}
				</div>
			</div>
		);
	}

	renderSnapshot() {
		return (
			<canvas
				ref={($ref) => this.$snapshotCanvas = $ref}
				class={s.snapshot}
				width={1200}
				height={1200}
			/>
		);
	}

	renderBackButton() {
		return (
			<button class={s.backContainer} onClick={this.backPhotoStep}>
				<div class={s.backArrow} />
				<HTML class={this.fontStyles.quizPhotoboothInfo} content={this.copy.$QUIZPHOTOBOOTH_BACK} markdown />
			</button>
		);
	}

	render({ photobooth, quizResult, isDesktopDevice, isDesktopWidth }) {
		const { waitingForCamera, headshotData, activeScreen, processingProgress } = this.state;
		const copy = this.copy;

		const photoboothBackgroundImage = isDesktopWidth ? photobooth.photoboothBackgroundDesktop : photobooth.photoboothBackgroundMobile;
		const isVideo = photoboothBackgroundImage?.endsWith('.mp4') || photoboothBackgroundImage?.endsWith('.webm');
		const photoboothBackgroundStyle = !isVideo && photoboothBackgroundImage ? { backgroundImage: `url(${photoboothBackgroundImage})` } : {};

		return (
			<div class={s.container} style={photoboothBackgroundStyle}>
				{!!photoboothBackgroundImage && isVideo && <video class={s.video} src={photoboothBackgroundImage} autoplay loop muted playsinline />}
				{this.renderBackButton()}
				{this.renderWelcomePage()}
				{this.renderBrowserNotAllowed()}
				{this.renderCameraPermissionsDenied()}
				{this.renderError()}
				{this.renderCameraFeed()}
				{this.renderSnapshot()}
				<div class={joinClasses(s.fadeOverlay, waitingForCamera && s.show)}>
					{/* {isDesktopDevice && <HTML content={copy.$QUIZPHOTOBOOTH_ENABLE_CAMERA} markdown />} */}
				</div>
				<Loader
					appData={this.appData}
					quizResult={quizResult}
					loading={activeScreen === 'loading'}
					progress={processingProgress}
					loadingAnimData={this.props.assets?.loadingAnim}
					isDesktopWidth={isDesktopWidth}
				/>
				<PhotoEdit
					appData={this.appData}
					copy={copy}
					colors={this.appData?.styling?.variables}
					headshotData={headshotData}
					retakePhoto={this.backPhotoStep}
					confirm={this.finishPhotoEdit}
					showEditScreen={activeScreen === 'photoEdit'}
					isDesktopDevice={isDesktopDevice}
					captureOrUpload={this.state.captureOrUpload}
					inputs={this.props.inputs}
				/>
			</div>
		);
	}
}
